import * as z from 'zod';

export const CreateContentRequest = z.object({
  content: z
    .object({
      _id: z
        .string()
        .describe('Translation content ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      schemaId: z
        .string()
        .describe('Translation schema ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      entityId: z
        .string()
        .describe(
          "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
        )
        .min(1)
        .max(100),
      locale: z.string().describe('Translation content locale.'),
      fields: z
        .record(
          z.string(),
          z.intersection(
            z.object({
              id: z
                .string()
                .describe(
                  "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                )
                .min(1)
                .max(256)
                .optional(),
              published: z
                .boolean()
                .describe(
                  'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                )
                .optional(),
              updatedBy: z
                .enum([
                  'UNKNOWN_UPDATER_IDENTITY',
                  'USER',
                  'EXTERNAL_APP',
                  'MACHINE',
                ])
                .optional(),
              schemaFieldKey: z
                .string()
                .describe(
                  'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                )
                .max(100)
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Field index. Used for cases where the order of the fields are relevant.'
                )
                .min(0)
                .optional(),
            }),
            z.xor([
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
              }),
              z.object({
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                textValue: z
                  .string()
                  .describe(
                    'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                  )
                  .max(100000),
              }),
              z.object({
                textValue: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                richContent: z
                  .any()
                  .describe(
                    'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                  ),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                image: z.string().describe('Wix Media Manager image.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                document: z.never().optional(),
                video: z.string().describe('Wix Media Manager video.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.string().describe('Wix Media Manager document.'),
              }),
            ])
          )
        )
        .describe(
          "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
        ),
      parentEntityId: z
        .string()
        .describe(
          'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      publishStatus: z
        .enum([
          'UNKNOWN_PUBLISH_STATUS',
          'UNPUBLISHED',
          'PARTIALLY_PUBLISHED',
          'PUBLISHED',
        ])
        .optional(),
      previewField: z
        .string()
        .describe(
          'Contains the value of the preview field if the schema defines one.'
        )
        .max(20000)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the translation content was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the translation content was updated.')
        .optional()
        .nullable(),
    })
    .describe('Translation content to create.'),
});
export const CreateContentResponse = z.object({
  _id: z
    .string()
    .describe('Translation content ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  schemaId: z
    .string()
    .describe('Translation schema ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  entityId: z
    .string()
    .describe(
      "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
    )
    .min(1)
    .max(100)
    .optional(),
  locale: z.string().describe('Translation content locale.').optional(),
  fields: z
    .record(
      z.string(),
      z.intersection(
        z.object({
          id: z
            .string()
            .describe(
              "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
            )
            .min(1)
            .max(256)
            .optional(),
          published: z
            .boolean()
            .describe(
              'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
            )
            .optional(),
          updatedBy: z
            .enum([
              'UNKNOWN_UPDATER_IDENTITY',
              'USER',
              'EXTERNAL_APP',
              'MACHINE',
            ])
            .describe(
              '**Required.** The source that updated the translation content.'
            )
            .optional(),
          schemaFieldKey: z
            .string()
            .describe(
              'Unique string identifier that links a specific content field to its corresponding field in the schema.'
            )
            .max(100)
            .optional(),
          index: z
            .number()
            .int()
            .describe(
              'Field index. Used for cases where the order of the fields are relevant.'
            )
            .min(0)
            .optional(),
        }),
        z.xor([
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
          }),
          z.object({
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            textValue: z
              .string()
              .describe(
                'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
              )
              .max(100000),
          }),
          z.object({
            textValue: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            richContent: z
              .any()
              .describe(
                'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
              ),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            image: z.string().describe('Wix Media Manager image.'),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            document: z.never().optional(),
            video: z.string().describe('Wix Media Manager video.'),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.string().describe('Wix Media Manager document.'),
          }),
        ])
      )
    )
    .describe(
      "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
    )
    .optional(),
  parentEntityId: z
    .string()
    .describe(
      'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
    )
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  publishStatus: z
    .enum([
      'UNKNOWN_PUBLISH_STATUS',
      'UNPUBLISHED',
      'PARTIALLY_PUBLISHED',
      'PUBLISHED',
    ])
    .describe('The aggregated published status across all content fields.')
    .optional(),
  previewField: z
    .string()
    .describe(
      'Contains the value of the preview field if the schema defines one.'
    )
    .max(20000)
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the translation content was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the translation content was updated.')
    .optional()
    .nullable(),
});
export const GetContentRequest = z.object({
  contentId: z
    .string()
    .describe('ID of the translation content to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetContentResponse = z.object({
  _id: z
    .string()
    .describe('Translation content ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  schemaId: z
    .string()
    .describe('Translation schema ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  entityId: z
    .string()
    .describe(
      "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
    )
    .min(1)
    .max(100)
    .optional(),
  locale: z.string().describe('Translation content locale.').optional(),
  fields: z
    .record(
      z.string(),
      z.intersection(
        z.object({
          id: z
            .string()
            .describe(
              "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
            )
            .min(1)
            .max(256)
            .optional(),
          published: z
            .boolean()
            .describe(
              'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
            )
            .optional(),
          updatedBy: z
            .enum([
              'UNKNOWN_UPDATER_IDENTITY',
              'USER',
              'EXTERNAL_APP',
              'MACHINE',
            ])
            .describe(
              '**Required.** The source that updated the translation content.'
            )
            .optional(),
          schemaFieldKey: z
            .string()
            .describe(
              'Unique string identifier that links a specific content field to its corresponding field in the schema.'
            )
            .max(100)
            .optional(),
          index: z
            .number()
            .int()
            .describe(
              'Field index. Used for cases where the order of the fields are relevant.'
            )
            .min(0)
            .optional(),
        }),
        z.xor([
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
          }),
          z.object({
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            textValue: z
              .string()
              .describe(
                'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
              )
              .max(100000),
          }),
          z.object({
            textValue: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            richContent: z
              .any()
              .describe(
                'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
              ),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            image: z.string().describe('Wix Media Manager image.'),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            document: z.never().optional(),
            video: z.string().describe('Wix Media Manager video.'),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.string().describe('Wix Media Manager document.'),
          }),
        ])
      )
    )
    .describe(
      "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
    )
    .optional(),
  parentEntityId: z
    .string()
    .describe(
      'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
    )
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  publishStatus: z
    .enum([
      'UNKNOWN_PUBLISH_STATUS',
      'UNPUBLISHED',
      'PARTIALLY_PUBLISHED',
      'PUBLISHED',
    ])
    .describe('The aggregated published status across all content fields.')
    .optional(),
  previewField: z
    .string()
    .describe(
      'Contains the value of the preview field if the schema defines one.'
    )
    .max(20000)
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the translation content was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the translation content was updated.')
    .optional()
    .nullable(),
});
export const UpdateContentRequest = z.object({
  _id: z
    .string()
    .describe('Translation content ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  content: z
    .object({
      _id: z
        .string()
        .describe('Translation content ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      schemaId: z
        .string()
        .describe('Translation schema ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      entityId: z
        .string()
        .describe(
          "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
        )
        .min(1)
        .max(100)
        .optional(),
      locale: z.string().describe('Translation content locale.').optional(),
      fields: z
        .record(
          z.string(),
          z.intersection(
            z.object({
              id: z
                .string()
                .describe(
                  "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                )
                .min(1)
                .max(256)
                .optional(),
              published: z
                .boolean()
                .describe(
                  'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                )
                .optional(),
              updatedBy: z
                .enum([
                  'UNKNOWN_UPDATER_IDENTITY',
                  'USER',
                  'EXTERNAL_APP',
                  'MACHINE',
                ])
                .optional(),
              schemaFieldKey: z
                .string()
                .describe(
                  'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                )
                .max(100)
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Field index. Used for cases where the order of the fields are relevant.'
                )
                .min(0)
                .optional(),
            }),
            z.xor([
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
              }),
              z.object({
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                textValue: z
                  .string()
                  .describe(
                    'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                  )
                  .max(100000),
              }),
              z.object({
                textValue: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                richContent: z
                  .any()
                  .describe(
                    'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                  ),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                image: z.string().describe('Wix Media Manager image.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                document: z.never().optional(),
                video: z.string().describe('Wix Media Manager video.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.string().describe('Wix Media Manager document.'),
              }),
            ])
          )
        )
        .describe(
          "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
        )
        .optional(),
      parentEntityId: z
        .string()
        .describe(
          'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      publishStatus: z
        .enum([
          'UNKNOWN_PUBLISH_STATUS',
          'UNPUBLISHED',
          'PARTIALLY_PUBLISHED',
          'PUBLISHED',
        ])
        .optional(),
      previewField: z
        .string()
        .describe(
          'Contains the value of the preview field if the schema defines one.'
        )
        .max(20000)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the translation content was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the translation content was updated.')
        .optional()
        .nullable(),
    })
    .describe('Translation content to update.'),
  options: z
    .object({
      forceFieldsTimestampUpdate: z
        .boolean()
        .describe(
          'If true each field updated in fields map will also update the timestamp fields\n\neven if the field value is not changed, otherwise the timestamp fields will be updated only if the field value has changed'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const UpdateContentResponse = z.object({
  _id: z
    .string()
    .describe('Translation content ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  schemaId: z
    .string()
    .describe('Translation schema ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  entityId: z
    .string()
    .describe(
      "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
    )
    .min(1)
    .max(100)
    .optional(),
  locale: z.string().describe('Translation content locale.').optional(),
  fields: z
    .record(
      z.string(),
      z.intersection(
        z.object({
          id: z
            .string()
            .describe(
              "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
            )
            .min(1)
            .max(256)
            .optional(),
          published: z
            .boolean()
            .describe(
              'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
            )
            .optional(),
          updatedBy: z
            .enum([
              'UNKNOWN_UPDATER_IDENTITY',
              'USER',
              'EXTERNAL_APP',
              'MACHINE',
            ])
            .describe(
              '**Required.** The source that updated the translation content.'
            )
            .optional(),
          schemaFieldKey: z
            .string()
            .describe(
              'Unique string identifier that links a specific content field to its corresponding field in the schema.'
            )
            .max(100)
            .optional(),
          index: z
            .number()
            .int()
            .describe(
              'Field index. Used for cases where the order of the fields are relevant.'
            )
            .min(0)
            .optional(),
        }),
        z.xor([
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
          }),
          z.object({
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            textValue: z
              .string()
              .describe(
                'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
              )
              .max(100000),
          }),
          z.object({
            textValue: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            richContent: z
              .any()
              .describe(
                'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
              ),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            video: z.never().optional(),
            document: z.never().optional(),
            image: z.string().describe('Wix Media Manager image.'),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            document: z.never().optional(),
            video: z.string().describe('Wix Media Manager video.'),
          }),
          z.object({
            textValue: z.never().optional(),
            richContent: z.never().optional(),
            image: z.never().optional(),
            video: z.never().optional(),
            document: z.string().describe('Wix Media Manager document.'),
          }),
        ])
      )
    )
    .describe(
      "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
    )
    .optional(),
  parentEntityId: z
    .string()
    .describe(
      'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
    )
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  publishStatus: z
    .enum([
      'UNKNOWN_PUBLISH_STATUS',
      'UNPUBLISHED',
      'PARTIALLY_PUBLISHED',
      'PUBLISHED',
    ])
    .describe('The aggregated published status across all content fields.')
    .optional(),
  previewField: z
    .string()
    .describe(
      'Contains the value of the preview field if the schema defines one.'
    )
    .max(20000)
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the translation content was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the translation content was updated.')
    .optional()
    .nullable(),
});
export const UpdateContentByKeyRequest = z.object({
  content: z
    .object({
      _id: z
        .string()
        .describe('Translation content ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      schemaId: z
        .string()
        .describe('Translation schema ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      entityId: z
        .string()
        .describe(
          "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
        )
        .min(1)
        .max(100),
      locale: z.string().describe('Translation content locale.'),
      fields: z
        .record(
          z.string(),
          z.intersection(
            z.object({
              id: z
                .string()
                .describe(
                  "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                )
                .min(1)
                .max(256)
                .optional(),
              published: z
                .boolean()
                .describe(
                  'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                )
                .optional(),
              updatedBy: z
                .enum([
                  'UNKNOWN_UPDATER_IDENTITY',
                  'USER',
                  'EXTERNAL_APP',
                  'MACHINE',
                ])
                .optional(),
              schemaFieldKey: z
                .string()
                .describe(
                  'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                )
                .max(100)
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Field index. Used for cases where the order of the fields are relevant.'
                )
                .min(0)
                .optional(),
            }),
            z.xor([
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
              }),
              z.object({
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                textValue: z
                  .string()
                  .describe(
                    'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                  )
                  .max(100000),
              }),
              z.object({
                textValue: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                richContent: z
                  .any()
                  .describe(
                    'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                  ),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                image: z.string().describe('Wix Media Manager image.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                document: z.never().optional(),
                video: z.string().describe('Wix Media Manager video.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.string().describe('Wix Media Manager document.'),
              }),
            ])
          )
        )
        .describe(
          "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
        )
        .optional(),
      parentEntityId: z
        .string()
        .describe(
          'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      publishStatus: z
        .enum([
          'UNKNOWN_PUBLISH_STATUS',
          'UNPUBLISHED',
          'PARTIALLY_PUBLISHED',
          'PUBLISHED',
        ])
        .optional(),
      previewField: z
        .string()
        .describe(
          'Contains the value of the preview field if the schema defines one.'
        )
        .max(20000)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the translation content was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the translation content was updated.')
        .optional()
        .nullable(),
    })
    .describe('Content to update.'),
  options: z
    .object({
      forceFieldsTimestampUpdate: z
        .boolean()
        .describe(
          'If true each field updated in fields map will also update the timestamp fields\n\neven if the field value is not changed, otherwise the timestamp fields will be updated only if the field value has changed'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const UpdateContentByKeyResponse = z.object({
  content: z
    .object({
      _id: z
        .string()
        .describe('Translation content ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      schemaId: z
        .string()
        .describe('Translation schema ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      entityId: z
        .string()
        .describe(
          "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
        )
        .min(1)
        .max(100)
        .optional(),
      locale: z.string().describe('Translation content locale.').optional(),
      fields: z
        .record(
          z.string(),
          z.intersection(
            z.object({
              id: z
                .string()
                .describe(
                  "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                )
                .min(1)
                .max(256)
                .optional(),
              published: z
                .boolean()
                .describe(
                  'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                )
                .optional(),
              updatedBy: z
                .enum([
                  'UNKNOWN_UPDATER_IDENTITY',
                  'USER',
                  'EXTERNAL_APP',
                  'MACHINE',
                ])
                .describe(
                  '**Required.** The source that updated the translation content.'
                )
                .optional(),
              schemaFieldKey: z
                .string()
                .describe(
                  'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                )
                .max(100)
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Field index. Used for cases where the order of the fields are relevant.'
                )
                .min(0)
                .optional(),
            }),
            z.xor([
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
              }),
              z.object({
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                textValue: z
                  .string()
                  .describe(
                    'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                  )
                  .max(100000),
              }),
              z.object({
                textValue: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                richContent: z
                  .any()
                  .describe(
                    'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                  ),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                video: z.never().optional(),
                document: z.never().optional(),
                image: z.string().describe('Wix Media Manager image.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                document: z.never().optional(),
                video: z.string().describe('Wix Media Manager video.'),
              }),
              z.object({
                textValue: z.never().optional(),
                richContent: z.never().optional(),
                image: z.never().optional(),
                video: z.never().optional(),
                document: z.string().describe('Wix Media Manager document.'),
              }),
            ])
          )
        )
        .describe(
          "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
        )
        .optional(),
      parentEntityId: z
        .string()
        .describe(
          'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      publishStatus: z
        .enum([
          'UNKNOWN_PUBLISH_STATUS',
          'UNPUBLISHED',
          'PARTIALLY_PUBLISHED',
          'PUBLISHED',
        ])
        .describe('The aggregated published status across all content fields.')
        .optional(),
      previewField: z
        .string()
        .describe(
          'Contains the value of the preview field if the schema defines one.'
        )
        .max(20000)
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the translation content was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the translation content was updated.')
        .optional()
        .nullable(),
    })
    .describe('Updated Content.')
    .optional(),
});
export const DeleteContentRequest = z.object({
  contentId: z
    .string()
    .describe('ID of the translation content to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteContentResponse = z.object({});
export const QueryContentsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryContentsResponse = z.object({
  contents: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Translation content ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        schemaId: z
          .string()
          .describe('Translation schema ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        entityId: z
          .string()
          .describe(
            "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
          )
          .min(1)
          .max(100)
          .optional(),
        locale: z.string().describe('Translation content locale.').optional(),
        fields: z
          .record(
            z.string(),
            z.intersection(
              z.object({
                id: z
                  .string()
                  .describe(
                    "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                  )
                  .min(1)
                  .max(256)
                  .optional(),
                published: z
                  .boolean()
                  .describe(
                    'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                  )
                  .optional(),
                updatedBy: z
                  .enum([
                    'UNKNOWN_UPDATER_IDENTITY',
                    'USER',
                    'EXTERNAL_APP',
                    'MACHINE',
                  ])
                  .describe(
                    '**Required.** The source that updated the translation content.'
                  )
                  .optional(),
                schemaFieldKey: z
                  .string()
                  .describe(
                    'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                  )
                  .max(100)
                  .optional(),
                index: z
                  .number()
                  .int()
                  .describe(
                    'Field index. Used for cases where the order of the fields are relevant.'
                  )
                  .min(0)
                  .optional(),
              }),
              z.xor([
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                }),
                z.object({
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  textValue: z
                    .string()
                    .describe(
                      'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                    )
                    .max(100000),
                }),
                z.object({
                  textValue: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  richContent: z
                    .any()
                    .describe(
                      'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                    ),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  image: z.string().describe('Wix Media Manager image.'),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  document: z.never().optional(),
                  video: z.string().describe('Wix Media Manager video.'),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.string().describe('Wix Media Manager document.'),
                }),
              ])
            )
          )
          .describe(
            "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
          )
          .optional(),
        parentEntityId: z
          .string()
          .describe(
            'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        publishStatus: z
          .enum([
            'UNKNOWN_PUBLISH_STATUS',
            'UNPUBLISHED',
            'PARTIALLY_PUBLISHED',
            'PUBLISHED',
          ])
          .describe(
            'The aggregated published status across all content fields.'
          )
          .optional(),
        previewField: z
          .string()
          .describe(
            'Contains the value of the preview field if the schema defines one.'
          )
          .max(20000)
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the translation content was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the translation content was updated.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const SearchContentsRequest = z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object for narrowing search results. For example, to return only translation content for a specific locale: `"filter": {"locale": "en"}`.'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(10)
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .string()
                  .describe(
                    'User-defined name of aggregation. Must be unique and will appear in aggregation results.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                    'DATE_HISTOGRAM',
                    'NESTED',
                  ])
                  .optional(),
                fieldPath: z
                  .string()
                  .describe(
                    'Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.'
                  )
                  .max(200)
                  .optional(),
                groupBy: z
                  .intersection(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'User-defined name of aggregation. Must be unique and will appear in aggregation results.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      fieldPath: z
                        .string()
                        .describe(
                          'Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.'
                        )
                        .max(200)
                        .optional(),
                    }),
                    z.xor([
                      z.object({ value: z.never().optional() }),
                      z.object({
                        value: z
                          .intersection(
                            z.object({
                              sortType: z
                                .enum(['COUNT', 'VALUE'])
                                .describe(
                                  'Whether to sort by number of matches or value of the field.'
                                )
                                .optional(),
                              sortDirection: z
                                .enum(['DESC', 'ASC'])
                                .describe(
                                  'Whether to sort in ascending or descending order.'
                                )
                                .optional(),
                              limit: z
                                .number()
                                .int()
                                .describe(
                                  'Number of aggregations to return.\n\nMin: `1`\nMax: `250`\nDefault: `10`'
                                )
                                .optional()
                                .nullable(),
                              missingValues: z
                                .enum(['EXCLUDE', 'INCLUDE'])
                                .describe(
                                  'Whether missing values should be included or excluded from the aggregation results.\n\nDefault: `EXCLUDE`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                includeOptions: z.never().optional(),
                              }),
                              z.object({
                                includeOptions: z
                                  .object({
                                    addToBucket: z
                                      .string()
                                      .describe(
                                        'Custom bucket name for missing values.\n\nDefault values:\n- string: `N/A`\n- int: `0`\n- bool: `false`'
                                      )
                                      .max(20)
                                      .optional(),
                                  })
                                  .describe(
                                    'Options for including missing values in the aggregation results.'
                                  ),
                              }),
                            ])
                          )
                          .describe('Value aggregation configuration.'),
                      }),
                    ])
                  )
                  .describe('Deprecated. Use `nested` instead.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  value: z
                    .intersection(
                      z.object({
                        sortType: z
                          .enum(['COUNT', 'VALUE'])
                          .describe(
                            'Whether to sort by number of matches or value of the field.'
                          )
                          .optional(),
                        sortDirection: z
                          .enum(['DESC', 'ASC'])
                          .describe(
                            'Whether to sort in ascending or descending order.'
                          )
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'Number of aggregations to return.\n\nMin: `1`\nMax: `250`\nDefault: `10`'
                          )
                          .optional()
                          .nullable(),
                        missingValues: z
                          .enum(['EXCLUDE', 'INCLUDE'])
                          .describe(
                            'Whether missing values should be included or excluded from the aggregation results.\n\nDefault: `EXCLUDE`'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ includeOptions: z.never().optional() }),
                        z.object({
                          includeOptions: z
                            .object({
                              addToBucket: z
                                .string()
                                .describe(
                                  'Custom bucket name for missing values.\n\nDefault values:\n- string: `N/A`\n- int: `0`\n- bool: `false`'
                                )
                                .max(20)
                                .optional(),
                            })
                            .describe(
                              'Options for including missing values in the aggregation results.'
                            ),
                        }),
                      ])
                    )
                    .describe('Value aggregation configuration.'),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if `to` is not provided.'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if `from` is not provided.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe('Range aggregation configuration.'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                        .describe(
                          'Operator for the scalar aggregation, for example `COUNT_DISTINCT`, `MIN`, `MAX`.'
                        )
                        .optional(),
                    })
                    .describe('Scalar aggregation configuration.'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  nested: z.never().optional(),
                  dateHistogram: z
                    .object({
                      interval: z
                        .enum([
                          'YEAR',
                          'MONTH',
                          'WEEK',
                          'DAY',
                          'HOUR',
                          'MINUTE',
                          'SECOND',
                        ])
                        .describe(
                          'Time interval for date histogram aggregation, for example `DAY`, `HOUR`, `MONTH`.'
                        )
                        .optional(),
                    })
                    .describe('Date histogram aggregation configuration.'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z
                    .object({
                      nestedAggregations: z
                        .array(
                          z.intersection(
                            z.object({
                              name: z
                                .string()
                                .describe(
                                  'User-defined name of aggregation. Must be unique and will appear in aggregation results.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                              type: z
                                .enum([
                                  'VALUE',
                                  'RANGE',
                                  'SCALAR',
                                  'DATE_HISTOGRAM',
                                ])
                                .optional(),
                              fieldPath: z
                                .string()
                                .describe(
                                  'Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.'
                                )
                                .max(200)
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                              }),
                              z.object({
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                value: z
                                  .intersection(
                                    z.object({
                                      sortType: z
                                        .enum(['COUNT', 'VALUE'])
                                        .describe(
                                          'Whether to sort by number of matches or value of the field.'
                                        )
                                        .optional(),
                                      sortDirection: z
                                        .enum(['DESC', 'ASC'])
                                        .describe(
                                          'Whether to sort in ascending or descending order.'
                                        )
                                        .optional(),
                                      limit: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Number of aggregations to return.\n\nMin: `1`\nMax: `250`\nDefault: `10`'
                                        )
                                        .optional()
                                        .nullable(),
                                      missingValues: z
                                        .enum(['EXCLUDE', 'INCLUDE'])
                                        .describe(
                                          'Whether missing values should be included or excluded from the aggregation results.\n\nDefault: `EXCLUDE`'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        includeOptions: z.never().optional(),
                                      }),
                                      z.object({
                                        includeOptions: z
                                          .object({
                                            addToBucket: z
                                              .string()
                                              .describe(
                                                'Custom bucket name for missing values.\n\nDefault values:\n- string: `N/A`\n- int: `0`\n- bool: `false`'
                                              )
                                              .max(20)
                                              .optional(),
                                          })
                                          .describe(
                                            'Options for including missing values in the aggregation results.'
                                          ),
                                      }),
                                    ])
                                  )
                                  .describe(
                                    'Value aggregation configuration. Calculates the distribution of field values within the dataset.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                range: z
                                  .object({
                                    buckets: z
                                      .array(
                                        z.object({
                                          from: z
                                            .number()
                                            .describe(
                                              'Inclusive lower bound of the range. Required if `to` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                          to: z
                                            .number()
                                            .describe(
                                              'Exclusive upper bound of the range. Required if `from` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                        })
                                      )
                                      .max(50)
                                      .optional(),
                                  })
                                  .describe(
                                    'Range aggregation configuration. Calculates counts within user-defined value ranges.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                scalar: z
                                  .object({
                                    type: z
                                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                      .describe(
                                        'Operator for the scalar aggregation, for example `COUNT_DISTINCT`, `MIN`, `MAX`.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Scalar aggregation configuration. Calculates single numerical metrics like count, min, max, sum, or average.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z
                                  .object({
                                    interval: z
                                      .enum([
                                        'YEAR',
                                        'MONTH',
                                        'WEEK',
                                        'DAY',
                                        'HOUR',
                                        'MINUTE',
                                        'SECOND',
                                      ])
                                      .describe(
                                        'Time interval for date histogram aggregation, for example `DAY`, `HOUR`, `MONTH`.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Date histogram aggregation configuration. Calculates counts within time intervals.'
                                  ),
                              }),
                            ])
                          )
                        )
                        .min(2)
                        .max(3)
                        .optional(),
                    })
                    .describe('Nested aggregation configuration.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression.')
              .max(100)
              .optional()
              .nullable(),
            fields: z.array(z.string()).max(20).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Whether to use auto fuzzy search (allowing typos by a managed proximity algorithm).'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database) or [ISO 8601 UTC offset format](https://en.wikipedia.org/wiki/ISO_8601#Time_offsets_from_UTC) for adjusting time fields in the specified filters and returned aggregation data. For example, `America/New_York`, `UTC`, or `+02:00`.\n\nDefault: Time zone specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              "Cursor-based paging for result navigation. When requesting `cursor_paging.cursor`,\n`filter`, `sort`, or `search` can't be specified."
            ),
        }),
      ])
    )
    .describe(
      'Search criteria including filter, sort, aggregations, and paging options.'
    ),
});
export const SearchContentsResponse = z.object({
  contents: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Translation content ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        schemaId: z
          .string()
          .describe('Translation schema ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        entityId: z
          .string()
          .describe(
            "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
          )
          .min(1)
          .max(100)
          .optional(),
        locale: z.string().describe('Translation content locale.').optional(),
        fields: z
          .record(
            z.string(),
            z.intersection(
              z.object({
                id: z
                  .string()
                  .describe(
                    "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                  )
                  .min(1)
                  .max(256)
                  .optional(),
                published: z
                  .boolean()
                  .describe(
                    'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                  )
                  .optional(),
                updatedBy: z
                  .enum([
                    'UNKNOWN_UPDATER_IDENTITY',
                    'USER',
                    'EXTERNAL_APP',
                    'MACHINE',
                  ])
                  .describe(
                    '**Required.** The source that updated the translation content.'
                  )
                  .optional(),
                schemaFieldKey: z
                  .string()
                  .describe(
                    'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                  )
                  .max(100)
                  .optional(),
                index: z
                  .number()
                  .int()
                  .describe(
                    'Field index. Used for cases where the order of the fields are relevant.'
                  )
                  .min(0)
                  .optional(),
              }),
              z.xor([
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                }),
                z.object({
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  textValue: z
                    .string()
                    .describe(
                      'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                    )
                    .max(100000),
                }),
                z.object({
                  textValue: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  richContent: z
                    .any()
                    .describe(
                      'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                    ),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  image: z.string().describe('Wix Media Manager image.'),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  document: z.never().optional(),
                  video: z.string().describe('Wix Media Manager video.'),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.string().describe('Wix Media Manager document.'),
                }),
              ])
            )
          )
          .describe(
            "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
          )
          .optional(),
        parentEntityId: z
          .string()
          .describe(
            'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        publishStatus: z
          .enum([
            'UNKNOWN_PUBLISH_STATUS',
            'UNPUBLISHED',
            'PARTIALLY_PUBLISHED',
            'PUBLISHED',
          ])
          .describe(
            'The aggregated published status across all content fields.'
          )
          .optional(),
        previewField: z
          .string()
          .describe(
            'Contains the value of the preview field if the schema defines one.'
          )
          .max(20000)
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the translation content was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the translation content was updated.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'User-defined name of aggregation as derived from search request.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Type of aggregation that must match specified kind as derived from search request.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe('Path to the field to aggregate by in dot notation.')
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'User-defined name of aggregation, matches the one specified in request.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe(
                                    'Type of aggregation that matches result.'
                                  )
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Path to the field to aggregate by in dot notation.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe(
                              'Date in [ISO 8601 format](https://en.wikipedia.org/wiki/ISO_8601).'
                            )
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of documents in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe(
                              'Map of nested aggregation results, keyed by aggregation name.'
                            )
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe('Aggregation data.')
    .optional(),
});
export const BulkCreateContentRequest = z.object({
  contents: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Translation content ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        schemaId: z
          .string()
          .describe('Translation schema ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        entityId: z
          .string()
          .describe(
            "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
          )
          .min(1)
          .max(100),
        locale: z.string().describe('Translation content locale.'),
        fields: z
          .record(
            z.string(),
            z.intersection(
              z.object({
                id: z
                  .string()
                  .describe(
                    "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                  )
                  .min(1)
                  .max(256)
                  .optional(),
                published: z
                  .boolean()
                  .describe(
                    'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                  )
                  .optional(),
                updatedBy: z
                  .enum([
                    'UNKNOWN_UPDATER_IDENTITY',
                    'USER',
                    'EXTERNAL_APP',
                    'MACHINE',
                  ])
                  .optional(),
                schemaFieldKey: z
                  .string()
                  .describe(
                    'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                  )
                  .max(100)
                  .optional(),
                index: z
                  .number()
                  .int()
                  .describe(
                    'Field index. Used for cases where the order of the fields are relevant.'
                  )
                  .min(0)
                  .optional(),
              }),
              z.xor([
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                }),
                z.object({
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  textValue: z
                    .string()
                    .describe(
                      'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                    )
                    .max(100000),
                }),
                z.object({
                  textValue: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  richContent: z
                    .any()
                    .describe(
                      'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                    ),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  video: z.never().optional(),
                  document: z.never().optional(),
                  image: z.string().describe('Wix Media Manager image.'),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  document: z.never().optional(),
                  video: z.string().describe('Wix Media Manager video.'),
                }),
                z.object({
                  textValue: z.never().optional(),
                  richContent: z.never().optional(),
                  image: z.never().optional(),
                  video: z.never().optional(),
                  document: z.string().describe('Wix Media Manager document.'),
                }),
              ])
            )
          )
          .describe(
            "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
          ),
        parentEntityId: z
          .string()
          .describe(
            'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        publishStatus: z
          .enum([
            'UNKNOWN_PUBLISH_STATUS',
            'UNPUBLISHED',
            'PARTIALLY_PUBLISHED',
            'PUBLISHED',
          ])
          .optional(),
        previewField: z
          .string()
          .describe(
            'Contains the value of the preview field if the schema defines one.'
          )
          .max(20000)
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the translation content was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the translation content was updated.')
          .optional()
          .nullable(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to include the created translation content items in the response. Set to `true` to receive the translation content items in the response.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateContentResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .max(256)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Translation content ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            schemaId: z
              .string()
              .describe('Translation schema ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            entityId: z
              .string()
              .describe(
                "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
              )
              .min(1)
              .max(100)
              .optional(),
            locale: z
              .string()
              .describe('Translation content locale.')
              .optional(),
            fields: z
              .record(
                z.string(),
                z.intersection(
                  z.object({
                    id: z
                      .string()
                      .describe(
                        "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    published: z
                      .boolean()
                      .describe(
                        'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                      )
                      .optional(),
                    updatedBy: z
                      .enum([
                        'UNKNOWN_UPDATER_IDENTITY',
                        'USER',
                        'EXTERNAL_APP',
                        'MACHINE',
                      ])
                      .describe(
                        '**Required.** The source that updated the translation content.'
                      )
                      .optional(),
                    schemaFieldKey: z
                      .string()
                      .describe(
                        'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                      )
                      .max(100)
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Field index. Used for cases where the order of the fields are relevant.'
                      )
                      .min(0)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                    }),
                    z.object({
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      textValue: z
                        .string()
                        .describe(
                          'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                        )
                        .max(100000),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      richContent: z
                        .any()
                        .describe(
                          'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                        ),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      image: z.string().describe('Wix Media Manager image.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      document: z.never().optional(),
                      video: z.string().describe('Wix Media Manager video.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z
                        .string()
                        .describe('Wix Media Manager document.'),
                    }),
                  ])
                )
              )
              .describe(
                "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
              )
              .optional(),
            parentEntityId: z
              .string()
              .describe(
                'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            publishStatus: z
              .enum([
                'UNKNOWN_PUBLISH_STATUS',
                'UNPUBLISHED',
                'PARTIALLY_PUBLISHED',
                'PUBLISHED',
              ])
              .describe(
                'The aggregated published status across all content fields.'
              )
              .optional(),
            previewField: z
              .string()
              .describe(
                'Contains the value of the preview field if the schema defines one.'
              )
              .max(20000)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the translation content was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the translation content was updated.')
              .optional()
              .nullable(),
          })
          .describe(
            'Translation content. This field is only returned if the operation was successful and `returnEntity` was set to true in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateContentRequest = z.object({
  contents: z
    .array(
      z.object({
        content: z
          .object({
            _id: z
              .string()
              .describe('Translation content ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            schemaId: z
              .string()
              .describe('Translation schema ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            entityId: z
              .string()
              .describe(
                "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
              )
              .min(1)
              .max(100)
              .optional(),
            locale: z
              .string()
              .describe('Translation content locale.')
              .optional(),
            fields: z
              .record(
                z.string(),
                z.intersection(
                  z.object({
                    id: z
                      .string()
                      .describe(
                        "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    published: z
                      .boolean()
                      .describe(
                        'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                      )
                      .optional(),
                    updatedBy: z
                      .enum([
                        'UNKNOWN_UPDATER_IDENTITY',
                        'USER',
                        'EXTERNAL_APP',
                        'MACHINE',
                      ])
                      .optional(),
                    schemaFieldKey: z
                      .string()
                      .describe(
                        'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                      )
                      .max(100)
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Field index. Used for cases where the order of the fields are relevant.'
                      )
                      .min(0)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                    }),
                    z.object({
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      textValue: z
                        .string()
                        .describe(
                          'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                        )
                        .max(100000),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      richContent: z
                        .any()
                        .describe(
                          'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                        ),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      image: z.string().describe('Wix Media Manager image.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      document: z.never().optional(),
                      video: z.string().describe('Wix Media Manager video.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z
                        .string()
                        .describe('Wix Media Manager document.'),
                    }),
                  ])
                )
              )
              .describe(
                "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
              )
              .optional(),
            parentEntityId: z
              .string()
              .describe(
                'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            publishStatus: z
              .enum([
                'UNKNOWN_PUBLISH_STATUS',
                'UNPUBLISHED',
                'PARTIALLY_PUBLISHED',
                'PUBLISHED',
              ])
              .optional(),
            previewField: z
              .string()
              .describe(
                'Contains the value of the preview field if the schema defines one.'
              )
              .max(20000)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the translation content was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the translation content was updated.')
              .optional()
              .nullable(),
          })
          .describe('Translation content to update.'),
        fieldMask: z.array(z.string()).optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to include the updated translation content items in the response. Set to `true` to receive the translation content items in the response.'
        )
        .optional(),
      forceFieldsTimestampUpdate: z
        .boolean()
        .describe(
          'If true each field updated in fields map will also update the timestamp fields\n\neven if the field value is not changed, otherwise the timestamp fields will be updated only if the field value has changed'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const BulkUpdateContentResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .max(256)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Translation content ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            schemaId: z
              .string()
              .describe('Translation schema ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            entityId: z
              .string()
              .describe(
                "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
              )
              .min(1)
              .max(100)
              .optional(),
            locale: z
              .string()
              .describe('Translation content locale.')
              .optional(),
            fields: z
              .record(
                z.string(),
                z.intersection(
                  z.object({
                    id: z
                      .string()
                      .describe(
                        "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    published: z
                      .boolean()
                      .describe(
                        'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                      )
                      .optional(),
                    updatedBy: z
                      .enum([
                        'UNKNOWN_UPDATER_IDENTITY',
                        'USER',
                        'EXTERNAL_APP',
                        'MACHINE',
                      ])
                      .describe(
                        '**Required.** The source that updated the translation content.'
                      )
                      .optional(),
                    schemaFieldKey: z
                      .string()
                      .describe(
                        'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                      )
                      .max(100)
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Field index. Used for cases where the order of the fields are relevant.'
                      )
                      .min(0)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                    }),
                    z.object({
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      textValue: z
                        .string()
                        .describe(
                          'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                        )
                        .max(100000),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      richContent: z
                        .any()
                        .describe(
                          'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                        ),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      image: z.string().describe('Wix Media Manager image.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      document: z.never().optional(),
                      video: z.string().describe('Wix Media Manager video.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z
                        .string()
                        .describe('Wix Media Manager document.'),
                    }),
                  ])
                )
              )
              .describe(
                "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
              )
              .optional(),
            parentEntityId: z
              .string()
              .describe(
                'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            publishStatus: z
              .enum([
                'UNKNOWN_PUBLISH_STATUS',
                'UNPUBLISHED',
                'PARTIALLY_PUBLISHED',
                'PUBLISHED',
              ])
              .describe(
                'The aggregated published status across all content fields.'
              )
              .optional(),
            previewField: z
              .string()
              .describe(
                'Contains the value of the preview field if the schema defines one.'
              )
              .max(20000)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the translation content was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the translation content was updated.')
              .optional()
              .nullable(),
          })
          .describe(
            'Translation content. This field is only returned if the operation was successful and `returnEntity` was set to true in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateContentByKeyRequest = z.object({
  contents: z
    .array(
      z.object({
        content: z
          .object({
            _id: z
              .string()
              .describe('Translation content ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            schemaId: z
              .string()
              .describe('Translation schema ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            entityId: z
              .string()
              .describe(
                "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
              )
              .min(1)
              .max(100),
            locale: z.string().describe('Translation content locale.'),
            fields: z
              .record(
                z.string(),
                z.intersection(
                  z.object({
                    id: z
                      .string()
                      .describe(
                        "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    published: z
                      .boolean()
                      .describe(
                        'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                      )
                      .optional(),
                    updatedBy: z
                      .enum([
                        'UNKNOWN_UPDATER_IDENTITY',
                        'USER',
                        'EXTERNAL_APP',
                        'MACHINE',
                      ])
                      .optional(),
                    schemaFieldKey: z
                      .string()
                      .describe(
                        'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                      )
                      .max(100)
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Field index. Used for cases where the order of the fields are relevant.'
                      )
                      .min(0)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                    }),
                    z.object({
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      textValue: z
                        .string()
                        .describe(
                          'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                        )
                        .max(100000),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      richContent: z
                        .any()
                        .describe(
                          'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                        ),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      image: z.string().describe('Wix Media Manager image.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      document: z.never().optional(),
                      video: z.string().describe('Wix Media Manager video.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z
                        .string()
                        .describe('Wix Media Manager document.'),
                    }),
                  ])
                )
              )
              .describe(
                "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
              )
              .optional(),
            parentEntityId: z
              .string()
              .describe(
                'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            publishStatus: z
              .enum([
                'UNKNOWN_PUBLISH_STATUS',
                'UNPUBLISHED',
                'PARTIALLY_PUBLISHED',
                'PUBLISHED',
              ])
              .optional(),
            previewField: z
              .string()
              .describe(
                'Contains the value of the preview field if the schema defines one.'
              )
              .max(20000)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the translation content was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the translation content was updated.')
              .optional()
              .nullable(),
          })
          .describe('Translation content to update.'),
        fieldMask: z.array(z.string()).optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to include the created translation content items in the response. Set to `true` to receive the translation content items in the response.'
        )
        .optional(),
      forceFieldsTimestampUpdate: z
        .boolean()
        .describe(
          'If true each field updated in fields map will also update the timestamp fields\n\neven if the field value is not changed, otherwise the timestamp fields will be updated only if the field value has changed'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const BulkUpdateContentByKeyResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .max(256)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Translation content ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            schemaId: z
              .string()
              .describe('Translation schema ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            entityId: z
              .string()
              .describe(
                "A unique identifier defined by the app developer to differentiate translation content for various entities within the site. For example, if an app developer is creating translation content for blog posts, the `entityId` can be `'blog-posts-1'`."
              )
              .min(1)
              .max(100)
              .optional(),
            locale: z
              .string()
              .describe('Translation content locale.')
              .optional(),
            fields: z
              .record(
                z.string(),
                z.intersection(
                  z.object({
                    id: z
                      .string()
                      .describe(
                        "Field unique identifier. Validated according to Regex `^[A-Za-z0-9-_)(]+$`. Field IDs may contain parentheses to reference repeated items, such as images. These parentheses can't be nested and each opening parenthesis must be followed by a closing one. For example, `title()()` is valid, while `title)(` and `title(())` aren't valid. The value inside the parenthesis is validated according to Regex `^[^)(\\\\]\\\\[\\\\.]+$`."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    published: z
                      .boolean()
                      .describe(
                        'Whether to publish the translation content to the live site, making it visible to site visitors.\n\nDefault: `false`'
                      )
                      .optional(),
                    updatedBy: z
                      .enum([
                        'UNKNOWN_UPDATER_IDENTITY',
                        'USER',
                        'EXTERNAL_APP',
                        'MACHINE',
                      ])
                      .describe(
                        '**Required.** The source that updated the translation content.'
                      )
                      .optional(),
                    schemaFieldKey: z
                      .string()
                      .describe(
                        'Unique string identifier that links a specific content field to its corresponding field in the schema.'
                      )
                      .max(100)
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Field index. Used for cases where the order of the fields are relevant.'
                      )
                      .min(0)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                    }),
                    z.object({
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      textValue: z
                        .string()
                        .describe(
                          'Plain text. Use this field if the corresponding schema `fields.type` is `SHORT_TEXT`, `LONG_TEXT`, `HTML`, or `IMAGE_LINK`.'
                        )
                        .max(100000),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      richContent: z
                        .any()
                        .describe(
                          'Translation Content rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" exampleid="71a5e527-a334-49e6-bc2d-9f5b3e831fc5">See Ricos document reference</widget>'
                        ),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      video: z.never().optional(),
                      document: z.never().optional(),
                      image: z.string().describe('Wix Media Manager image.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      document: z.never().optional(),
                      video: z.string().describe('Wix Media Manager video.'),
                    }),
                    z.object({
                      textValue: z.never().optional(),
                      richContent: z.never().optional(),
                      image: z.never().optional(),
                      video: z.never().optional(),
                      document: z
                        .string()
                        .describe('Wix Media Manager document.'),
                    }),
                  ])
                )
              )
              .describe(
                "List of fields for the translation content. This property uses a string to map to a `ContentField` (`Map<string, ContentField>`). The string serves as a key, which you'll need to access each field in the content and when referencing a translation schema."
              )
              .optional(),
            parentEntityId: z
              .string()
              .describe(
                'A reference to the parent content. For example, if the content is a menu item, this property would contain the `entityId` of the menu it belongs to.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            publishStatus: z
              .enum([
                'UNKNOWN_PUBLISH_STATUS',
                'UNPUBLISHED',
                'PARTIALLY_PUBLISHED',
                'PUBLISHED',
              ])
              .describe(
                'The aggregated published status across all content fields.'
              )
              .optional(),
            previewField: z
              .string()
              .describe(
                'Contains the value of the preview field if the schema defines one.'
              )
              .max(20000)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the translation content was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the translation content was updated.')
              .optional()
              .nullable(),
          })
          .describe(
            'Translation content. This field is only returned if the operation was successful and `returnEntity` was set to true in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkDeleteContentRequest = z.object({
  contentIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteContentResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .max(256)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const GetContentWordCountRequest = z.object({
  sourceLanguage: z
    .string()
    .describe(
      "Source language for the translatable content (typically a site's primary language), in IETF BCP 47 language tag format. For example, `en-US` for English as used in the U.S."
    ),
  options: z
    .object({
      targetLanguage: z
        .string()
        .describe(
          'Target language for translation content to include in the response. When omitted, returns only source language statistics without translation progress metrics, in IETF BCP 47 language tag format. For example, `fr-FR` for French as used in France.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetContentWordCountResponse = z.object({
  sourceWordCount: z
    .number()
    .int()
    .describe(
      'Number of words in the source language across all translation content.'
    )
    .optional(),
  translatedWordCount: z
    .number()
    .int()
    .describe(
      'Number of words translated into the target language. Returned only when `targetLanguage` is specified.'
    )
    .optional()
    .nullable(),
  outdatedTranslatedWordCount: z
    .number()
    .int()
    .describe(
      'Number of translated words that are outdated due to source content modifications after translation. Returned only when `targetLanguage` is specified.'
    )
    .optional()
    .nullable(),
  totalFieldCount: z
    .number()
    .int()
    .describe(
      'Number of fields in the source language across all translation schemas.'
    )
    .optional(),
  translatedFieldCount: z
    .number()
    .int()
    .describe(
      'Number of fields translated into the target language. Returned only when `targetLanguage` is specified.'
    )
    .optional()
    .nullable(),
  outdatedTranslatedFieldCount: z
    .number()
    .int()
    .describe(
      'Number of translated fields that are outdated due to source content modifications after translation. Returned only when `targetLanguage` is specified.'
    )
    .optional()
    .nullable(),
  schemaWordCount: z
    .array(
      z.object({
        schemaId: z
          .string()
          .describe('Translation schema ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        sourceWordCount: z
          .number()
          .int()
          .describe('Number of words in the source language for this schema.')
          .optional(),
        translatedWordCount: z
          .number()
          .int()
          .describe(
            'Number of words translated into the target language for this schema. Returned only when `targetLanguage` is specified.'
          )
          .optional()
          .nullable(),
        outdatedTranslatedWordCount: z
          .number()
          .int()
          .describe(
            'Number of translated words that are outdated due to source content modifications after translation for this schema. Returned only when `targetLanguage` is specified.'
          )
          .optional()
          .nullable(),
        totalFieldCount: z
          .number()
          .int()
          .describe('Number of fields in the source language for this schema.')
          .optional(),
        translatedFieldCount: z
          .number()
          .int()
          .describe(
            'Number of fields translated into the target language for this schema. Returned only when `targetLanguage` is specified.'
          )
          .optional()
          .nullable(),
        outdatedTranslatedFieldCount: z
          .number()
          .int()
          .describe(
            'Number of translated fields that are outdated due to source content modifications after translation for this schema. Returned only when `targetLanguage` is specified.'
          )
          .optional()
          .nullable(),
      })
    )
    .max(500)
    .optional(),
});
